import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getQuotes, deleteQuote, downloadQuotePdf, convertQuoteToInvoice, sendQuoteEmail, getSettings, duplicateQuote, updateQuoteStatus } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  MoreHorizontal, 
  Download, 
  Trash2,
  FileEdit,
  Eye,
  FileText,
  ArrowRightCircle,
  Send,
  Printer,
  Loader2,
  Mail,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';

const StatusBadge = ({ status, t }) => {
  const styles = {
    draft: 'bg-slate-50 text-slate-700 border border-slate-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    sent: 'bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    accepted: 'bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    rejected: 'bg-red-50 text-red-700 border border-red-200 px-2.5 py-0.5 rounded-full text-xs font-medium',
    expired: 'bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-0.5 rounded-full text-xs font-medium'
  };
  const getLabel = (status) => {
    if (t) {
      switch(status) {
        case 'draft': return t('invoices.draft');
        case 'sent': return t('invoices.sent');
        case 'accepted': return t('quotes.accepted');
        case 'rejected': return t('quotes.rejected');
        case 'expired': return t('quotes.expired');
        default: return status;
      }
    }
    return status;
  };
  return <span className={styles[status] || styles.draft}>{getLabel(status)}</span>;
};

const QuotesPage = () => {
  const { t, language } = useLanguage();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailQuote, setEmailQuote] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: '', to_name: '', subject: '', body_text: '' });
  const navigate = useNavigate();

  const getLocale = () => {
    switch(language) {
      case 'de': return 'de-CH';
      case 'en': return 'en-GB';
      default: return 'fr-CH';
    }
  };

  const formatCurrency = (amount, currency = 'CHF') => {
    return new Intl.NumberFormat(getLocale(), { style: 'currency', currency }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(getLocale());
  };

  useEffect(() => {
    loadQuotes();
    checkSMTPConfig();
  }, []);

  const checkSMTPConfig = async () => {
    try {
      const response = await getSettings();
      setSmtpConfigured(response.data.smtp_configured || false);
    } catch (error) {
      console.error('Error checking SMTP config');
    }
  };

  const loadQuotes = async () => {
    try {
      const response = await getQuotes();
      setQuotes(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  const handleViewPdf = async (quoteId) => {
    try {
      const response = await downloadQuotePdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDownloadPdf = async (quoteId, quoteNumber) => {
    try {
      const response = await downloadQuotePdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devis_${quoteNumber}.pdf`;
      link.click();
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleConvertToInvoice = async (quoteId) => {
    try {
      const response = await convertQuoteToInvoice(quoteId);
      toast.success(`Facture ${response.data.invoice_number} créée`);
      navigate('/app/invoices');
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    }
  };

  const handleDuplicate = async (quoteId) => {
    try {
      const response = await duplicateQuote(quoteId);
      toast.success(`Devis ${response.data.quote_number} cree`);
      loadQuotes();
    } catch (error) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handlePrint = async (quoteId) => {
    try {
      const response = await downloadQuotePdf(quoteId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }
    } catch (error) {
      toast.error('Erreur impression');
    }
  };

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await updateQuoteStatus(quoteId, newStatus);
      toast.success('Statut mis a jour');
      loadQuotes();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleSendEmail = (quote) => {
    if (!smtpConfigured) {
      const clientEmail = quote.client?.email || '';
      const subject = encodeURIComponent(`Devis ${quote.quote_number} - ${quote.total} ${quote.currency || 'CHF'}`);
      const body = encodeURIComponent(
        `Bonjour,\n\n` +
        `Veuillez trouver ci-joint le devis ${quote.quote_number} d'un montant de ${formatCurrency(quote.total, quote.currency || 'CHF')}.\n\n` +
        `Validité: ${quote.valid_until ? formatDate(quote.valid_until) : 'Non spécifiée'}\n\n` +
        `N'hésitez pas à nous contacter pour toute question.\n\n` +
        `Cordialement`
      );
      window.location.href = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
      toast.info('Configurez votre SMTP dans Paramètres > Email pour envoyer directement.', { duration: 5000 });
      return;
    }

    setEmailQuote(quote);
    setEmailForm({
      to_email: quote.client?.email || '',
      to_name: quote.client?.name || '',
      subject: `Devis ${quote.quote_number} - ${formatCurrency(quote.total, quote.currency || 'CHF')}`,
      body_text: `Bonjour${quote.client?.name ? ' ' + quote.client.name : ''},\n\nVeuillez trouver ci-joint le devis ${quote.quote_number} d'un montant de ${formatCurrency(quote.total, quote.currency || 'CHF')}.\n\nValidité: ${quote.valid_until ? formatDate(quote.valid_until) : 'À votre convenance'}\n\nN'hésitez pas à nous contacter pour toute question.\n\nCordialement`
    });
    setEmailDialogOpen(true);
  };

  const handleSendEmailSubmit = async () => {
    if (!emailForm.to_email) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    setSendingEmail(true);
    try {
      await sendQuoteEmail(emailQuote.id, {
        to_email: emailForm.to_email,
        to_name: emailForm.to_name,
        subject: emailForm.subject,
        body_text: emailForm.body_text,
        document_type: 'quote',
        document_id: emailQuote.id
      });
      toast.success('Devis envoyé par email avec succès !');
      setEmailDialogOpen(false);
      loadQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleDelete = async () => {
    if (!quoteToDelete) return;
    try {
      await deleteQuote(quoteToDelete);
      toast.success('Devis supprimé');
      loadQuotes();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setDeleteDialogOpen(false);
      setQuoteToDelete(null);
    }
  };

  const filteredQuotes = quotes.filter((quote) => {
    const matchesSearch = 
      quote.quote_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.client?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quotes-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">{t('quotes.title')}</h1>
          <p className="text-slate-500 mt-1">{language === 'fr' ? 'Gérez vos devis et convertissez-les en factures' : language === 'de' ? 'Verwalten Sie Ihre Offerten und wandeln Sie sie in Rechnungen um' : 'Manage your quotes and convert them to invoices'}</p>
        </div>
        <Button onClick={() => navigate('/app/quotes/new')} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          {t('quotes.create')}
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder={t('common.search') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="draft">{t('invoices.draft')}</SelectItem>
                <SelectItem value="sent">{t('invoices.sent')}</SelectItem>
                <SelectItem value="accepted">{t('quotes.accepted')}</SelectItem>
                <SelectItem value="rejected">{t('quotes.rejected')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {filteredQuotes.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="px-6 py-4">{t('quotes.number')}</th>
                    <th className="px-6 py-4">{t('quotes.client')}</th>
                    <th className="px-6 py-4">{t('quotes.date')}</th>
                    <th className="px-6 py-4">{t('quotes.validUntil')}</th>
                    <th className="px-6 py-4">{t('quotes.amount')}</th>
                    <th className="px-6 py-4">{t('quotes.status')}</th>
                    <th className="px-6 py-4 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id}>
                      <td className="px-6 py-4 font-medium text-slate-900">{quote.quote_number}</td>
                      <td className="px-6 py-4 text-slate-600">{quote.client?.name || '-'}</td>
                      <td className="px-6 py-4 text-slate-600">{formatDate(quote.created_at)}</td>
                      <td className="px-6 py-4 text-slate-600">{quote.valid_until ? formatDate(quote.valid_until) : '-'}</td>
                      <td className="px-6 py-4 font-medium text-slate-900">{formatCurrency(quote.total, quote.currency)}</td>
                      <td className="px-6 py-4"><StatusBadge status={quote.status} t={t} /></td>
                      <td className="px-6 py-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewPdf(quote.id)}>
                              <Eye className="w-4 h-4 mr-2" />{t('common.preview')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadPdf(quote.id, quote.quote_number)}>
                              <Download className="w-4 h-4 mr-2" />{t('common.download')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrint(quote.id)}>
                              <Printer className="w-4 h-4 mr-2" />{language === 'fr' ? 'Imprimer' : language === 'de' ? 'Drucken' : 'Print'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendEmail(quote)}>
                              <Send className="w-4 h-4 mr-2" />{t('common.send')}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicate(quote.id)}>
                              <Copy className="w-4 h-4 mr-2" />{t('common.duplicate')}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {quote.status === 'draft' && (
                              <DropdownMenuItem onClick={() => handleStatusChange(quote.id, 'sent')}>
                                <Send className="w-4 h-4 mr-2" />{language === 'fr' ? 'Marquer comme transmis' : language === 'de' ? 'Als gesendet markieren' : 'Mark as sent'}
                              </DropdownMenuItem>
                            )}
                            {(quote.status === 'sent' || quote.status === 'draft') && (
                              <DropdownMenuItem onClick={() => handleStatusChange(quote.id, 'accepted')}>
                                <ArrowRightCircle className="w-4 h-4 mr-2" />{language === 'fr' ? 'Marquer comme accepte' : language === 'de' ? 'Als akzeptiert markieren' : 'Mark as accepted'}
                              </DropdownMenuItem>
                            )}
                            {quote.status !== 'accepted' && (
                              <DropdownMenuItem onClick={() => handleConvertToInvoice(quote.id)}>
                                <ArrowRightCircle className="w-4 h-4 mr-2" />{t('quotes.convertToInvoice')}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => { setQuoteToDelete(quote.id); setDeleteDialogOpen(true); }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />{t('common.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state py-12">
              <FileEdit className="empty-state-icon" />
              <h3 className="empty-state-title">{t('quotes.noQuotes')}</h3>
              <p className="empty-state-description">{t('quotes.createFirst')}</p>
              <Button onClick={() => navigate('/app/quotes/new')} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" />{t('quotes.create')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('common.delete')} ?</AlertDialogTitle>
            <AlertDialogDescription>{language === 'fr' ? 'Cette action est irréversible.' : language === 'de' ? 'Diese Aktion kann nicht rückgängig gemacht werden.' : 'This action cannot be undone.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Envoyer le devis par email
            </DialogTitle>
            <DialogDescription>
              {emailQuote?.quote_number} - {emailQuote && formatCurrency(emailQuote.total, emailQuote.currency || 'CHF')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email destinataire *</Label>
                <Input 
                  type="email"
                  value={emailForm.to_email}
                  onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })}
                  placeholder="client@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input 
                  value={emailForm.to_name}
                  onChange={(e) => setEmailForm({ ...emailForm, to_name: e.target.value })}
                  placeholder="Nom du client"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sujet</Label>
              <Input 
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Message</Label>
              <Textarea 
                value={emailForm.body_text}
                onChange={(e) => setEmailForm({ ...emailForm, body_text: e.target.value })}
                rows={6}
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📎 Pièce jointe :</strong> Le PDF du devis sera automatiquement attaché.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSendEmailSubmit} 
              disabled={sendingEmail}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sendingEmail ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Envoyer</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuotesPage;
